import React, { useState } from 'react';
import { submitContact } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { companyInfo } from '../../data/mock';
import { useLanguage } from '../../context/LanguageContext';
import {
  Phone,
  Mail,
  Clock,
  Send,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const ContactPage = () => {
  const { t } = useLanguage();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await submitContact(subject, message);
      setIsSubmitted(true);
      toast.success(t('portalUi.client.contact.toastSuccess', 'Ziņojums nosūtīts!'));
      setSubject('');
      setMessage('');
      setTimeout(() => setIsSubmitted(false), 3000);
    } catch (error) {
      toast.error(t('portalUi.client.contact.toastError', 'Kļūda nosūtot ziņojumu'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="client-contact">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#2E2E2E]" data-testid="client-contact-title"><span className="gradient-text">{t('portalUi.client.contact.title', 'Kontakti')}</span></h1>
        <p className="text-[#2E2E2E]/60 mt-1" data-testid="client-contact-subtitle">
          {t('portalUi.client.contact.subtitlePrefix', 'Uzdodiet jautājumu vai')} <span className="text-[#1F3D2B] font-semibold">{t('portalUi.client.contact.subtitleHighlight', 'sazinieties ar mums')}</span>
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Contact Info */}
        <div className="space-y-4">
          <Card className="border-0 shadow-sm hover-lift card-shine" data-testid="client-contact-phone-card">
            <CardContent className="p-6">
              <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-4 group" data-testid="client-contact-phone-link">
                <div className="w-12 h-12 rounded-xl bg-[#1F3D2B]/10 flex items-center justify-center group-hover:bg-[#1F3D2B] transition-colors">
                  <Phone className="w-6 h-6 text-[#1F3D2B] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.client.contact.phoneLabel', 'Tālrunis')}</p>
                  <p className="font-semibold text-[#1F3D2B]">{companyInfo.phone}</p>
                </div>
              </a>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm hover-lift card-shine" data-testid="client-contact-email-card">
            <CardContent className="p-6">
              <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-4 group" data-testid="client-contact-email-link">
                <div className="w-12 h-12 rounded-xl bg-[#1F3D2B]/10 flex items-center justify-center group-hover:bg-[#1F3D2B] transition-colors">
                  <Mail className="w-6 h-6 text-[#1F3D2B] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.client.contact.emailLabel', 'E-pasts')}</p>
                  <p className="font-semibold text-[#1F3D2B]">{companyInfo.email}</p>
                </div>
              </a>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm hover-lift card-shine" data-testid="client-contact-hours-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#1F3D2B]/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-[#1F3D2B]" />
                </div>
                <div>
                  <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.client.contact.hoursLabel', 'Darba laiks')}</p>
                  <p className="font-semibold text-[#2E2E2E]">{companyInfo.workingHours}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Contact Form */}
        <div className="lg:col-span-2">
          <Card className="border-0 shadow-sm card-shine" data-testid="client-contact-form">
            <CardHeader>
              <CardTitle className="text-lg">{t('portalUi.client.contact.formTitle', 'Nosūtīt ziņojumu')}</CardTitle>
            </CardHeader>
            <CardContent>
              {isSubmitted ? (
                <div className="text-center py-12" data-testid="client-contact-success">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#2E2E2E] mb-2">{t('portalUi.client.contact.successTitle', 'Ziņojums nosūtīts!')}</h3>
                  <p className="text-[#2E2E2E]/60">{t('portalUi.client.contact.successBody', 'Mēs ar Jums sazināsimies.')}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="client-contact-form-element">
                  <div className="space-y-2">
                    <Label htmlFor="subject">{t('portalUi.client.contact.subjectLabel', 'Tēma')}</Label>
                    <Input
                      id="subject"
                      value={subject}
                      data-testid="client-contact-subject"
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder={t('portalUi.client.contact.subjectPlaceholder', 'Ziņojuma tēma')}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">{t('portalUi.client.contact.messageLabel', 'Ziņojums')}</Label>
                    <Textarea
                      id="message"
                      value={message}
                      data-testid="client-contact-message"
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={t('portalUi.client.contact.messagePlaceholder', 'Jūsu jautājums vai ziņojums...')}
                      rows={6}
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className="bg-[#1F3D2B] hover:bg-[#2a5039] text-white"
                    disabled={isSubmitting}
                    data-testid="client-contact-submit"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {t('portalUi.client.contact.submitting', 'Nosūta...')}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        {t('portalUi.client.contact.submit', 'Nosūtīt')}
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
